import type { ResourceCategoryV2 } from "../fields/form-entity-select";
import { RuleVariable, RuleVariablesState } from "../hooks/useRuleVariables";
import {
	AFTER_LAST_SENTINEL_ID,
	AFTER_LAST_SENTINEL_POSITION,
	CONTINUE_SENTINEL_ID,
	CONTINUE_SENTINEL_POSITION,
	FOREACH_SENTINEL_ID,
	FOREACH_SENTINEL_POSITION,
} from "../nodes/loop/loopTypes";
import { OperatorOption } from "../types/configs";
import { reconnectableForEdgeType,type RuleEdge, type RuleEdgeType } from "../types/edges";
import { AssignmentStatement, DecisionStatement, type LoopNodeData, RuleNode } from "../types/nodes";
import { CategorizedOperators } from "../types/rule-lifecycle";
import { isDecisionValueEntityRow } from "./decision-statement-value-utils";
import { mapApiFormulaVariableRef, mapApiScreenFieldRef } from "./data-field-type-utils";
import { normalizeVariableFormulaFromApi } from "./formula-variable-payload";
import { normalizeStopTagNames } from "./stop-tag-utils";

// Kept local to avoid a circular-import chain through useRuleLifecycle.
/** Same `when` ↔ edge mapping as `rule-transitions-utils` (loop uses forEach / endLoop). */
const LOOP_WHEN_TO_EDGE_META: Record<string, { type: RuleEdgeType; sourceHandle: string }> = {
	"always": { type: "default", sourceHandle: "out" },
	"true": { type: "decision-true", sourceHandle: "true" },
	"false": { type: "decision-false", sourceHandle: "false" },
	"forEach": { type: "loop-start", sourceHandle: "loop-start" },
	"endLoop": { type: "loop-end", sourceHandle: "loop-end" },
};

const DEFAULT_LOOP_MARKER_END = {
	type: "arrow",
	color: "#b3b5b9",
	height: 20,
	width: 20,
} as const;

const LOOP_SENTINEL_TARGET_IDS = new Set<string>([
	FOREACH_SENTINEL_ID,
	AFTER_LAST_SENTINEL_ID,
	CONTINUE_SENTINEL_ID,
]);

/**
 * Maps a saved transition `to` back to a sub-canvas target, restoring the sentinels.
 *
 * - `endLoop` → End sentinel (the `to` was the loop's main-canvas successor or the loop id).
 * - `to === loopNodeId` (next iteration) → Continue sentinel.
 * - decision `true`/`false` pointing off this loop's body (early exit) → End sentinel.
 * - self transition (`from === to`) → sentinel implied by `when` (endLoop → End, else Continue).
 * - otherwise the target is a real body node, kept as-is.
 */
function resolveInnerLoopTransitionTarget(
	loopNodeId: string,
	from: string,
	to: string,
	when: string,
	loopBodyNodeIds: Set<string>,
): string {
	const whenNorm = when.toLowerCase();

	if (whenNorm === "endloop") return AFTER_LAST_SENTINEL_ID;

	// node → itself: a self-edge to a sentinel, encoded by `when`.
	if (from === to) {
		return whenNorm === "endloop" ? AFTER_LAST_SENTINEL_ID : CONTINUE_SENTINEL_ID;
	}

	if (to === loopNodeId) return CONTINUE_SENTINEL_ID;

	// Decision branch leaving the loop body (not a body node / sentinel) is an early loop exit.
	if (whenNorm === "true" || whenNorm === "false") {
		const inLoopBody = loopBodyNodeIds.has(to) || LOOP_SENTINEL_TARGET_IDS.has(to);
		if (!inLoopBody) return AFTER_LAST_SENTINEL_ID;
	}

	return to;
}

/** Saved transitions collapse ForEach / Continue sources onto `loopNodeId`; inner persisted edges use ForEach as entry source. */
function resolveInnerLoopTransitionSource(loopNodeId: string, from: string): string {
	if (from !== loopNodeId) return from;
	return FOREACH_SENTINEL_ID;
}

/** Transition rows may omit sourceHandle; nested loop exit uses loop-end meta. */
function metaForHydratedInnerLoopEdge(
	e: any,
	whenStr: string,
	target: string,
	source: string,
): { type: RuleEdgeType; sourceHandle: string } {
	const whenNorm = String(whenStr ?? "").toLowerCase();

	// Flat `when:forEach` + `to:loopId` → Continue sentinel: Start→Continue vs body→Continue.
	if (whenNorm === "foreach" && target === CONTINUE_SENTINEL_ID) {
		if (source === FOREACH_SENTINEL_ID) {
			return LOOP_WHEN_TO_EDGE_META["forEach"];
		}
		const sh = e.sourceHandle ?? e.sourcehandle;
		if (sh === "true") return { type: "decision-true", sourceHandle: "true" };
		if (sh === "false") return { type: "decision-false", sourceHandle: "false" };
		const out = typeof sh === "string" && sh !== "" ? sh : "out";
		return { type: "loop-for-each-return", sourceHandle: out };
	}

	const base = LOOP_WHEN_TO_EDGE_META[whenNorm] ?? LOOP_WHEN_TO_EDGE_META["always"];
	if (whenNorm !== "endloop" || target !== AFTER_LAST_SENTINEL_ID) {
		return base;
	}
	const sh = e.sourceHandle ?? e.sourcehandle;
	if (sh === "true") return { type: "decision-true", sourceHandle: "true" };
	if (sh === "false") return { type: "decision-false", sourceHandle: "false" };
	if (sh === "afterLast-out") return { type: "loop-end", sourceHandle: "afterLast-out" };
	// `when: endLoop` marks Loop End target; edge type still follows the source handle.
	return { type: "default", sourceHandle: typeof sh === "string" && sh !== "" && sh !== "loop-end" ? sh : "out" };
}

/**
 * Persisted RuleEdges may target parent loop id or a sibling only on the parent sub-canvas
 * (nested case). Those IDs are not in this loop's loopNodes, so `filterPersistedLoopBodyEdges`
 * would drop them — retarget to After Last sentinel (same as collapsed transition `to`).
 */
function normalizeLoopEdgesOffCanvasExitTargets(
	loopNodeId: string,
	loopNodes: RuleNode[],
	edges: RuleEdge[],
): RuleEdge[] {
	const bodyIds = new Set(loopNodes.map((n) => n.id));
	const hub = new Set<string>([FOREACH_SENTINEL_ID, AFTER_LAST_SENTINEL_ID, CONTINUE_SENTINEL_ID]);

	return edges.map((e) => {
		const targetId = e.target != null ? String(e.target) : "";
		const sourceId = e.source != null ? String(e.source) : "";

		if (targetId === AFTER_LAST_SENTINEL_ID || targetId === "") return e;

		const targetInSubcanvas = bodyIds.has(targetId) || hub.has(targetId);
		if (targetInSubcanvas) return e;

		const sourceInSubcanvas = hub.has(sourceId) || bodyIds.has(sourceId);
		if (!sourceInSubcanvas) return e;

		const looksLikeLoopExit =
			e.type === "decision-false" ||
			e.type === "decision-true" ||
			e.type === "default" ||
			e.sourceHandle === "true" ||
			e.sourceHandle === "false" ||
			e.type === "loop-end" ||
			e.type === "loop-for-each-return" ||
			e.sourceHandle === "loop-end" ||
			e.sourceHandle === "afterLast-out" ||
			e.sourceHandle === "out" ||
			targetId === loopNodeId;

		if (!looksLikeLoopExit) return e;

		const sh = typeof e.sourceHandle === "string" && e.sourceHandle !== "" ? e.sourceHandle : "out";
		return {
			...e,
			target: AFTER_LAST_SENTINEL_ID,
			targetHandle: "in",
			id: e.id != null ? String(e.id) : `edge__${sourceId}-${sh}-${AFTER_LAST_SENTINEL_ID}`,
		} as RuleEdge;
	});
}

/**
 * Recursively collects every node ID that lives inside a nested loop's
 * loopNodes (at any depth). Used to strip grandchildren that the backend
 * may have included in an ancestor loop's loopNodes list.
 */
function collectDescendantLoopNodeIds(nodes: RuleNode[]): Set<string> {
	const ids = new Set<string>();
	for (const node of nodes) {
		if (node.type === "loop") {
			const inner = (node.data as LoopNodeData).loopNodes ?? [];
			for (const child of inner) {
				ids.add(child.id);
				collectDescendantLoopNodeIds([child]).forEach((id) => ids.add(id));
			}
		}
	}
	return ids;
}

/**
 * Maps a raw node from the API to the frontend RuleNode shape.
 * Converts numeric IDs to strings and normalizes per-type statement fields.
 */
/** Resolve display name for a `variables.databaseTable` row (API shape varies). */
const databaseTableVariableRowName = (row: any): string => {
	if (!row || typeof row !== "object") return "";
	const qb =
		row.rulenodequerybuilder ??
		row.ruleNodeQueryBuilder ??
		row.rule_node_query_builder ??
		null;
	const fromQb = typeof qb?.name === "string" ? qb.name.trim() : "";
	if (fromQb) return fromQb;
	return typeof row.name === "string" ? row.name.trim() : "";
};

export const mapApiNodeToRuleNode = (
	apiNode: any,
	operators: CategorizedOperators,
	simpleVariables?: { id: number; name: string }[],
	reactionNodes?: { id: number; name: string; data: { name: string } }[],
	/** `rule.variables.databaseTable` — used to hydrate loop `databaseTableVariableName` from `databaseTableVariableId`. */
	databaseTableVariables?: any[],
): RuleNode => {
	const node = {
		...apiNode,
		id: String(apiNode.id),
		data: { ...apiNode.data },
	};

	if (node.data.type === "assignment" && Array.isArray(apiNode.data?.statements)) {
		node.data.statements = apiNode.data.statements.map((s: any): AssignmentStatement => {
			const variableId = s.targetVariableId ?? s.simpleVariableId;
			let variableName = s.variable ?? "";
			if (!variableName && variableId != null && simpleVariables) {
				const match = simpleVariables.find((v) => String(v.id) === String(variableId));
				variableName = match?.name ?? String(variableId);
			}

			const valueDatabaseTableFieldId =
				s.valueDatabaseTableFieldId != null
					? String(s.valueDatabaseTableFieldId)
					: s.valueTargetDatabaseFieldId != null
						? String(s.valueTargetDatabaseFieldId)
						: undefined;
			const valueScreenFieldId =
				s.valueScreenFieldId != null
					? String(s.valueScreenFieldId)
					: s.valueTargetScreenFieldId != null
						? String(s.valueTargetScreenFieldId)
						: undefined;

			const valueSimpleVariableId =
				s.valueSimpleVariableId != null ? String(s.valueSimpleVariableId) : undefined;
			const valueFormulaVariableId =
				s.valueFormulaVariableId != null ? String(s.valueFormulaVariableId) : undefined;
			const valueDatabaseSingleRowVariableId =
				s.valueDatabaseSingleRowVariableId != null
					? String(s.valueDatabaseSingleRowVariableId)
					: undefined;

			let valueSimpleVariableName: string | undefined;
			if (s.valueSimpleVariableId != null) {
				const match = simpleVariables?.find((v) => String(v.id) === String(s.valueSimpleVariableId));
				valueSimpleVariableName = match?.name ?? String(s.valueSimpleVariableId);
			} else if (typeof s.valueSimpleVariable?.name === "string") {
				valueSimpleVariableName = s.valueSimpleVariable.name;
			} else if (typeof s.valueSimpleVariableName === "string") {
				valueSimpleVariableName = s.valueSimpleVariableName;
			}

			const valueFormulaVariableName =
				typeof s.valueFormulaVariable?.name === "string"
					? s.valueFormulaVariable.name
					: typeof s.valueFormulaVariableName === "string"
						? s.valueFormulaVariableName
						: undefined;
			const valueDatabaseSingleRowVariableName =
				typeof s.valueDatabaseSingleRowVariableName === "string"
					? s.valueDatabaseSingleRowVariableName
					: undefined;
			const valueGlobalConstant =
				typeof s.valueGlobalConstant === "string" ? s.valueGlobalConstant : undefined;

			const valueEntity = isDecisionValueEntityRow({
				valueDatabaseTableFieldId,
				valueScreenFieldId,
				valueSimpleVariableId,
				valueFormulaVariableId,
				valueDatabaseSingleRowVariableId,
				valueGlobalConstant,
				valueSimpleVariableName,
				valueFormulaVariableName,
				valueDatabaseSingleRowVariableName,
			});

			const plainTextFromApi = s.plainTextValue ?? s.valueLiteral;
			const plainTextValue = valueEntity
				? undefined
				: (plainTextFromApi ?? (typeof s.value === "string" ? s.value : ""));

			const valueUi = valueEntity
				? (typeof s.value === "string" && s.value
					? s.value
					: valueDatabaseTableFieldId ??
							valueScreenFieldId ??
							valueSimpleVariableName ??
							valueFormulaVariableName ??
							valueDatabaseSingleRowVariableName ??
							valueGlobalConstant ??
							"")
				: (plainTextValue ?? "");

			return {
				id: s.id != null ? String(s.id) : undefined,
				variable: variableName,
				operator: normalizeAssignmentOperatorFromApi(s.operator, s.operatorId, operators.assignment),
				value: valueUi,
				plainTextValue,
				valueDatabaseTableFieldId,
				valueScreenFieldId,
				valueScreenField: mapApiScreenFieldRef(s.valueScreenField),
				valueSimpleVariableId,
				valueFormulaVariableId,
				valueDatabaseSingleRowVariableId,
				valueGlobalConstant,
				valueGlobalConstantName:
					typeof s.valueGlobalConstantName === "string"
						? s.valueGlobalConstantName
						: typeof s.valueGlobalConstant?.name === "string"
							? s.valueGlobalConstant.name
							: undefined,
				valueSimpleVariableName,
				valueFormulaVariableName,
				valueFormulaVariable: mapApiFormulaVariableRef(s.valueFormulaVariable),
				valueDatabaseSingleRowVariableName,
				valueDatabaseTableField: s.valueDatabaseTableField
					? { name: s.valueDatabaseTableField.name }
					: undefined,
				valueScreenTableField:
					mapApiScreenFieldRef(s.valueScreenField) ??
					mapApiScreenFieldRef(s.valueScreenTableField),
			};
		});
	}

	if (node.data.type === "decision" && Array.isArray(apiNode.data?.statements)) {
		node.data.statements = apiNode.data.statements.map((s: any, i: number): DecisionStatement => {
			const databaseTableFieldId =
				s.resourceDatabaseTableFieldId != null
					? String(s.resourceDatabaseTableFieldId)
					: s.targetDatabaseFieldId != null
						? String(s.targetDatabaseFieldId)
						: undefined;
			const screenFieldId =
				s.resourceScreenFieldId != null
					? String(s.resourceScreenFieldId)
					: s.targetScreenFieldId != null
						? String(s.targetScreenFieldId)
						: undefined;
			const advancedTriggerId =
				s.resourceAdvancedTriggerId != null
					? String(s.resourceAdvancedTriggerId)
					: undefined;
			const resourceCollectionId =
				s.resourceCollectionId != null ? String(s.resourceCollectionId) : undefined;

			const resourceReactionNodeId = s.resourceReactionId || null;
			const resourceReactionName = s.resourceReaction?.rulenode?.name ?? undefined;

			let simpleVariableName: string | undefined;
			if (s.resourceSimpleVariableId != null) {
				const match = simpleVariables?.find((v) => String(v.id) === String(s.resourceSimpleVariableId));
				simpleVariableName = match?.name ?? String(s.resourceSimpleVariableId);
			} else if (s.simpleVariableId != null) {
				const match = simpleVariables?.find((v) => String(v.id) === String(s.simpleVariableId));
				simpleVariableName = match?.name ?? String(s.simpleVariableId);
			}

			const resourceFormulaVariableName =
				typeof s.resourceFormulaVariable?.name === "string"
					? s.resourceFormulaVariable.name
					: undefined;

			const valueDatabaseTableFieldId =
				s.valueDatabaseTableFieldId != null
					? String(s.valueDatabaseTableFieldId)
					: s.valueTargetDatabaseFieldId != null
						? String(s.valueTargetDatabaseFieldId)
						: undefined;
			const valueScreenFieldId =
				s.valueScreenFieldId != null
					? String(s.valueScreenFieldId)
					: s.valueTargetScreenFieldId != null
						? String(s.valueTargetScreenFieldId)
						: undefined;
			const valueAdvancedTriggerId =
				s.valueAdvancedTriggerId != null
					? String(s.valueAdvancedTriggerId)
					: undefined;
			const valueResourceCollectionId =
				s.valueResourceCollectionId != null ? String(s.valueResourceCollectionId) : undefined;

			const valueSimpleVariableId =
				s.valueSimpleVariableId != null ? String(s.valueSimpleVariableId) : undefined;
			const valueFormulaVariableId =
				s.valueFormulaVariableId != null ? String(s.valueFormulaVariableId) : undefined;
			const valueDatabaseSingleRowVariableId =
				s.valueDatabaseSingleRowVariableId != null
					? String(s.valueDatabaseSingleRowVariableId)
					: undefined;

			let valueSimpleVariableName: string | undefined;
			if (s.valueSimpleVariableId != null) {
				const match = simpleVariables?.find((v) => String(v.id) === String(s.valueSimpleVariableId));
				valueSimpleVariableName = match?.name ?? String(s.valueSimpleVariableId);
			} else if (typeof s.valueSimpleVariable?.name === "string") {
				valueSimpleVariableName = s.valueSimpleVariable.name;
			} else if (typeof s.valueSimpleVariableName === "string") {
				valueSimpleVariableName = s.valueSimpleVariableName;
			}

			const valueFormulaVariableName =
				typeof s.valueFormulaVariable?.name === "string"
					? s.valueFormulaVariable.name
					: typeof s.valueFormulaVariableName === "string"
						? s.valueFormulaVariableName
						: undefined;
			const valueDatabaseSingleRowVariableName =
				typeof s.valueDatabaseSingleRowVariableName === "string"
					? s.valueDatabaseSingleRowVariableName
					: undefined;
			const valueGlobalConstant =
				typeof s.valueGlobalConstant === "string" ? s.valueGlobalConstant : undefined;

			const valueCollectionName =
				typeof s.valueResourceCollection?.name === "string"
					? s.valueResourceCollection.name
					: typeof s.valueCollectionName === "string"
						? s.valueCollectionName
						: undefined;

			const valueEntity = isDecisionValueEntityRow({
				valueDatabaseTableFieldId,
				valueScreenFieldId,
				valueAdvancedTriggerId,
				valueResourceCollectionId,
				valueSimpleVariableId,
				valueFormulaVariableId,
				valueDatabaseSingleRowVariableId,
				valueGlobalConstant,
				valueSimpleVariableName,
				valueFormulaVariableName,
				valueDatabaseSingleRowVariableName,
			});

			const plainTextFromApi = s.plainTextValue ?? s.valueLiteral;
			const plainTextValue = valueEntity
				? undefined
				: (plainTextFromApi ?? (typeof s.value === "string" ? s.value : ""));

			const valueUi = valueEntity
				? (typeof s.value === "string" && s.value
					? s.value
					: valueDatabaseTableFieldId ??
							valueScreenFieldId ??
							valueAdvancedTriggerId ??
							valueResourceCollectionId ??
							valueSimpleVariableName ??
							valueFormulaVariableName ??
							valueDatabaseSingleRowVariableName ??
							valueGlobalConstant ??
							"")
				: (plainTextValue ?? "");

			const resourceFormulaVariableId =
				s.resourceFormulaVariableId != null ? String(s.resourceFormulaVariableId) : undefined;
			const resourceDatabaseSingleRowVariableId =
				s.resourceDatabaseSingleRowVariableId != null
					? String(s.resourceDatabaseSingleRowVariableId)
					: undefined;

			const operatorResolved =
				s.operatorId != null
					? resolveOperatorId(s.operatorId, operators.comparative)
					: typeof s.operator === "object" && s.operator != null && "operator" in s.operator
						? String((s.operator as { operator?: string }).operator ?? "")
						: typeof s.operator === "string"
							? s.operator
							: "";

			return {
				id: s.id != null ? String(s.id) : undefined,
				num: s.num ?? i + 1,
				databaseTableFieldId,
				screenFieldId,
				advancedTriggerId,
				resourceCollectionId,
				collectionName:
					typeof s.resourceCollection?.name === "string"
						? s.resourceCollection.name
						: typeof s.collectionName === "string"
							? s.collectionName
							: undefined,
				databaseTableField: s.resourceDatabaseTableField
					? { name: s.resourceDatabaseTableField.name }
					: s.databaseTableField
						? { name: s.databaseTableField.name }
						: undefined,
				screenTableField:
					mapApiScreenFieldRef(s.resourceScreenField) ??
					mapApiScreenFieldRef(s.screenField),
				advancedTrigger: s.resourceAdvancedTrigger
					? { name: s.resourceAdvancedTrigger.friendlyName ?? s.resourceAdvancedTrigger.name }
					: s.advancedTrigger
						? { name: s.advancedTrigger.friendlyName ?? s.advancedTrigger.name }
						: undefined,
				resourceCollection: s.resourceCollection
					? { name: s.resourceCollection.name }
					: resourceCollectionId && typeof s.collectionName === "string"
						? { name: s.collectionName }
						: undefined,
				simpleVariableName,
				resourceFormulaVariableId,
				formulaVariableName: resourceFormulaVariableName,
				resourceFormulaVariable: mapApiFormulaVariableRef(s.resourceFormulaVariable),
				resourceReactionNodeId,
				resourceReactionName,
				resource:
					resourceReactionNodeId ??
					databaseTableFieldId ??
					screenFieldId ??
					advancedTriggerId ??
					resourceCollectionId ??
					resourceFormulaVariableId ??
					resourceDatabaseSingleRowVariableId ??
					simpleVariableName ??
					"",
				operator: operatorResolved,
				plainTextValue,
				value: valueUi,
				valueDatabaseTableFieldId,
				valueScreenFieldId,
				valueAdvancedTriggerId,
				valueSimpleVariableId,
				valueFormulaVariableId,
				valueDatabaseSingleRowVariableId,
				valueGlobalConstant,
				valueGlobalConstantName:
					typeof s.valueGlobalConstantName === "string"
						? s.valueGlobalConstantName
						: typeof s.valueGlobalConstant?.name === "string"
							? s.valueGlobalConstant.name
							: undefined,
				valueSimpleVariableName,
				valueFormulaVariableName,
				valueFormulaVariable: mapApiFormulaVariableRef(s.valueFormulaVariable),
				valueDatabaseSingleRowVariableName,
				valueDatabaseTableField: s.valueDatabaseTableField
					? { name: s.valueDatabaseTableField.name }
					: undefined,
				valueScreenField: mapApiScreenFieldRef(s.valueScreenField),
				valueScreenTableField:
					mapApiScreenFieldRef(s.valueScreenField) ??
					mapApiScreenFieldRef(s.valueScreenTableField),
				valueAdvancedTrigger: s.valueAdvancedTrigger
					? { name: s.valueAdvancedTrigger.friendlyName ?? s.valueAdvancedTrigger.name }
					: undefined,
				valueResourceCollectionId,
				valueCollectionName,
				valueResourceCollection: s.valueResourceCollection
					? { name: s.valueResourceCollection.name }
					: valueResourceCollectionId && valueCollectionName
						? { name: valueCollectionName }
						: undefined,
			};
		});
	}

	if (node.data.type === "query_builder") {
		if (node.data.databaseTableId != null) node.data.databaseTableId = String(node.data.databaseTableId);
		if (node.data.sortByFieldId != null) node.data.sortByFieldId = String(node.data.sortByFieldId);

		node.data.databaseTableName = apiNode.data.databaseTable?.name ?? apiNode.data.databaseTableName ?? "";

		const rawConditions = apiNode.data.statements ?? apiNode.data.conditions ?? [];
		if (Array.isArray(rawConditions)) {
			node.data.conditions = rawConditions.map((c: any) => ({
				databaseTableFieldId: c.databaseTableFieldId != null ? String(c.databaseTableFieldId) : "",
				databaseTableFieldName: c.databaseTableField?.name ?? c.databaseTableFieldName ?? "",
				operator: c.operatorId != null ? resolveOperatorId(c.operatorId, operators.comparative) : (c.operator ?? ""),
				value: c.valueLiteral ?? c.value ?? "",
			}));
		}

		delete node.data.databaseTable;
		delete node.data.statements;
	}

	if (node.data.type === "filter") {
		if (node.data.databaseTableId != null) node.data.databaseTableId = String(node.data.databaseTableId);
		if (node.data.screenTableId != null) node.data.screenTableId = String(node.data.screenTableId);
		if (node.data.databaseTableVariableId != null) node.data.databaseTableVariableId = String(node.data.databaseTableVariableId);
		if (node.data.sortByDatabaseTableFieldId != null) node.data.sortByDatabaseTableFieldId = String(node.data.sortByDatabaseTableFieldId);
		if (node.data.sortByScreenTableFieldId != null) node.data.sortByScreenTableFieldId = String(node.data.sortByScreenTableFieldId);
		delete node.data.sortBy;

		node.data.tableName = apiNode.data.databaseTableName ?? apiNode.data.screenTableName ?? apiNode.data.tableName ?? "";
		node.data.recordScope = apiNode.data.recordScope ?? apiNode.data.rowSelection ?? "firstRecord";

		const rawConditions = apiNode.data.statements ?? apiNode.data.conditions ?? [];
		if (Array.isArray(rawConditions)) {
			node.data.conditions = rawConditions.map((c: any) => {
				const valueDatabaseTableFieldId =
					c.valueDatabaseTableFieldId != null
						? String(c.valueDatabaseTableFieldId)
						: c.valueTargetDatabaseFieldId != null
							? String(c.valueTargetDatabaseFieldId)
							: undefined;
				const valueScreenFieldId =
					c.valueScreenFieldId != null
						? String(c.valueScreenFieldId)
						: c.valueTargetScreenFieldId != null
							? String(c.valueTargetScreenFieldId)
							: undefined;

				const valueSimpleVariableId =
					c.valueSimpleVariableId != null ? String(c.valueSimpleVariableId) : undefined;
				const valueFormulaVariableId =
					c.valueFormulaVariableId != null ? String(c.valueFormulaVariableId) : undefined;
				const valueDatabaseSingleRowVariableId =
					c.valueDatabaseSingleRowVariableId != null
						? String(c.valueDatabaseSingleRowVariableId)
						: undefined;

				let valueSimpleVariableName: string | undefined;
				if (c.valueSimpleVariableId != null) {
					const match = simpleVariables?.find((v) => String(v.id) === String(c.valueSimpleVariableId));
					valueSimpleVariableName = match?.name ?? String(c.valueSimpleVariableId);
				} else if (typeof c.valueSimpleVariable?.name === "string") {
					valueSimpleVariableName = c.valueSimpleVariable.name;
				} else if (typeof c.valueSimpleVariableName === "string") {
					valueSimpleVariableName = c.valueSimpleVariableName;
				}

				const valueFormulaVariableName =
					typeof c.valueFormulaVariable?.name === "string"
						? c.valueFormulaVariable.name
						: typeof c.valueFormulaVariableName === "string"
							? c.valueFormulaVariableName
							: undefined;
				const valueDatabaseSingleRowVariableName =
					typeof c.valueDatabaseSingleRowVariableName === "string"
						? c.valueDatabaseSingleRowVariableName
						: undefined;
				const valueGlobalConstant =
					typeof c.valueGlobalConstant === "string" ? c.valueGlobalConstant : undefined;

				const valueEntity = isDecisionValueEntityRow({
					valueDatabaseTableFieldId,
					valueScreenFieldId,
					valueSimpleVariableId,
					valueFormulaVariableId,
					valueDatabaseSingleRowVariableId,
					valueGlobalConstant,
					valueSimpleVariableName,
					valueFormulaVariableName,
					valueDatabaseSingleRowVariableName,
				});

				const plainTextFromApi = c.plainTextValue ?? c.valueLiteral;
				const plainTextValue = valueEntity
					? undefined
					: (plainTextFromApi ?? (typeof c.value === "string" ? c.value : ""));

				const valueUi = valueEntity
					? (typeof c.value === "string" && c.value
						? c.value
						: valueDatabaseTableFieldId ??
								valueScreenFieldId ??
								valueSimpleVariableName ??
								valueFormulaVariableName ??
								valueDatabaseSingleRowVariableName ??
								valueGlobalConstant ??
								"")
					: (plainTextValue ?? "");

				const operatorResolved =
					c.operatorId != null
						? resolveOperatorId(c.operatorId, operators.comparative)
						: typeof c.operator === "object" && c.operator != null && "operator" in c.operator
							? String((c.operator as { operator?: string }).operator ?? "")
							: typeof c.operator === "string"
								? c.operator
								: "";

				return {
					field: c.databaseTableField?.name ?? c.screenTableField?.name ?? "",
					databaseTableFieldId: c.databaseTableFieldId != null ? String(c.databaseTableFieldId) : undefined,
					screenTableFieldId: c.screenTableFieldId != null ? String(c.screenTableFieldId) : undefined,
					operator: operatorResolved,
					value: valueUi,
					plainTextValue,
					valueDatabaseTableFieldId,
					valueScreenFieldId,
					valueSimpleVariableId,
					valueFormulaVariableId,
					valueDatabaseSingleRowVariableId,
					valueGlobalConstant,
					valueGlobalConstantName:
						typeof c.valueGlobalConstantName === "string"
							? c.valueGlobalConstantName
							: typeof c.valueGlobalConstant?.name === "string"
								? c.valueGlobalConstant.name
								: undefined,
					valueSimpleVariableName,
					valueFormulaVariableName,
					valueFormulaVariable: mapApiFormulaVariableRef(c.valueFormulaVariable),
					valueDatabaseSingleRowVariableName,
					valueDatabaseTableFieldName: c.valueDatabaseTableField?.name,
					valueScreenFieldName: c.valueScreenField?.name ?? c.valueScreenTableField?.name,
					valueScreenField: mapApiScreenFieldRef(c.valueScreenField),
					valueScreenTableField:
						mapApiScreenFieldRef(c.valueScreenField) ??
						mapApiScreenFieldRef(c.valueScreenTableField),
				};
			});
		}

		delete node.data.statements;
		delete node.data.databaseTableName;
		delete node.data.screenTableName;
		delete node.data.rowSelection;
	}

	if (node.data.type === "sort") {
		if (node.data.databaseTableId != null) node.data.databaseTableId = String(node.data.databaseTableId);
		if (node.data.screenTableId != null) node.data.screenTableId = String(node.data.screenTableId);
		if (node.data.databaseTableVariableId != null) node.data.databaseTableVariableId = String(node.data.databaseTableVariableId);

		const rawStatements = apiNode.data.statements ?? apiNode.data.conditions ?? [];

		// database_table_name may be absent from the API response; fall back to the
		// table name embedded in the first statement's field object.
		const derivedTableName =
			(rawStatements[0]?.databaseTableField ?? rawStatements[0]?.sortByDatabaseTableField)?.tableName
			?? (rawStatements[0]?.screenTableField ?? rawStatements[0]?.sortByScreenTableField)?.tableName
			?? "";

		node.data.tableName =
			apiNode.data.databaseTableName
			?? apiNode.data.screenTableName
			?? apiNode.data.tableName
			?? derivedTableName;

		if (Array.isArray(rawStatements)) {
			node.data.conditions = rawStatements.map((s: any) => ({
				sortByDatabaseTableFieldId: s.sortByDatabaseTableFieldId != null ? String(s.sortByDatabaseTableFieldId) : undefined,
				sortByScreenTableFieldId: s.sortByScreenTableFieldId != null ? String(s.sortByScreenTableFieldId) : undefined,
				sortByFieldName: (s.sortByDatabaseTableField ?? s.databaseTableField)?.name
					?? (s.sortByScreenTableField ?? s.screenTableField)?.name
					?? "",
				sortOrder: s.sortOrder ?? "asc",
			}));
		}

		delete node.data.statements;
		delete node.data.databaseTableName;
		delete node.data.screenTableName;
	}

	if (node.data.type === "loop") {
		const ld = node.data as Record<string, unknown>;
		ld.direction = ld.sortOrder;

		const hadServerVariableName =
			typeof apiNode.data?.databaseTableVariableName === "string" &&
			String(apiNode.data.databaseTableVariableName).trim() !== "";

		if (hadServerVariableName) {
			delete ld.databaseTableId;
			delete ld.databaseTableVariableId;
			delete ld.tableName;
			delete ld.databaseTableName;
			delete ld.screenTableName;
		} else {
			if (ld.databaseTableId != null) ld.databaseTableId = String(ld.databaseTableId);
			if (ld.screenTableId != null) ld.screenTableId = String(ld.screenTableId);
			if (ld.databaseTableVariableId != null) ld.databaseTableVariableId = String(ld.databaseTableVariableId);

			const varId = ld.databaseTableVariableId;
			if (varId != null && databaseTableVariables?.length) {
				const match = databaseTableVariables.find((v: any) => String(v?.id) === String(varId));
				const resolved = databaseTableVariableRowName(match);
				if (resolved) {
					ld.databaseTableVariableName = resolved;
				}
			}
		}

		// Persist `loopType` on the node when the API omits it — UI reads this only (no binding guess in forms).
		const lt = ld.loopType;
		if (lt === "variable" || lt === "table") {
			ld.loopType = lt;
		} else {
			if (ld.collectionId != null) ld.collectionId = String(ld.collectionId);

			const hasTableBinding = Boolean(
				(ld.screenTableId != null && String(ld.screenTableId).trim() !== "") ||
					(ld.databaseTableId != null && String(ld.databaseTableId).trim() !== "") ||
					(ld.collectionId != null && String(ld.collectionId).trim() !== "") ||
					(ld.databaseTableVariableId != null && String(ld.databaseTableVariableId).trim() !== "") ||
					(typeof ld.databaseTableVariableName === "string" && ld.databaseTableVariableName.trim() !== "") ||
					(typeof ld.collectionName === "string" && ld.collectionName.trim() !== "") ||
					(typeof ld.tableName === "string" && ld.tableName.trim() !== ""),
			);
			ld.loopType = hasTableBinding ? "table" : "variable";
		}

		// Normalise sentinel positions: accept {x,y} objects or {forEachX/forEachY}
		// flat fields that some backend versions may emit.
		const rawFE = ld.startSentinelPosition as any;
		if (rawFE != null && typeof rawFE === "object" && "x" in rawFE && "y" in rawFE) {
			ld.startSentinelPosition = { x: Number(rawFE.x), y: Number(rawFE.y) };
		} else if (typeof ld.forEachSentinelX === "number" || typeof ld.forEachSentinelY === "number") {
			ld.startSentinelPosition = {
				x: typeof ld.forEachSentinelX === "number" ? ld.forEachSentinelX : FOREACH_SENTINEL_POSITION.x,
				y: typeof ld.forEachSentinelY === "number" ? ld.forEachSentinelY : FOREACH_SENTINEL_POSITION.y,
			};
		}

		const rawAL = ld.endSentinelPosition as any;
		if (rawAL != null && typeof rawAL === "object" && "x" in rawAL && "y" in rawAL) {
			ld.endSentinelPosition = { x: Number(rawAL.x), y: Number(rawAL.y) };
		} else if (typeof ld.afterLastSentinelX === "number" || typeof ld.afterLastSentinelY === "number") {
			ld.endSentinelPosition = {
				x: typeof ld.afterLastSentinelX === "number" ? ld.afterLastSentinelX : AFTER_LAST_SENTINEL_POSITION.x,
				y: typeof ld.afterLastSentinelY === "number" ? ld.afterLastSentinelY : AFTER_LAST_SENTINEL_POSITION.y,
			};
		}

		const rawCS = ld.continueSentinelPosition as any;
		if (rawCS != null && typeof rawCS === "object" && "x" in rawCS && "y" in rawCS) {
			ld.continueSentinelPosition = { x: Number(rawCS.x), y: Number(rawCS.y) };
		} else if (typeof ld.continueSentinelX === "number" || typeof ld.continueSentinelY === "number") {
			ld.continueSentinelPosition = {
				x: typeof ld.continueSentinelX === "number" ? ld.continueSentinelX : CONTINUE_SENTINEL_POSITION.x,
				y: typeof ld.continueSentinelY === "number" ? ld.continueSentinelY : CONTINUE_SENTINEL_POSITION.y,
			};
		}

		// Recursively map nested loop nodes so their IDs and statements are
		// normalized the same way top-level nodes are on load.
		if (Array.isArray(ld.loopNodes)) {
			const mapped = (ld.loopNodes as any[]).map((innerNode: any) => {
				const mappedNode = mapApiNodeToRuleNode(
					innerNode,
					operators,
					simpleVariables,
					reactionNodes,
					databaseTableVariables,
				);
				const scope = innerNode.loopNodeId ?? innerNode.data?.loopNodeId;
				if (scope == null) return mappedNode;
				const scopeStr = String(scope);
				return {
					...mappedNode,
					loopNodeId: scopeStr,
					data: { ...mappedNode.data, loopNodeId: scopeStr },
				};
			});
			const scopedLoopNodes = mapped as RuleNode[];
			const descendantIds = collectDescendantLoopNodeIds(scopedLoopNodes);
			ld.loopNodes = descendantIds.size > 0
				? scopedLoopNodes.filter((n) => !descendantIds.has(n.id))
				: scopedLoopNodes;
		}

		// Normalize loopEdges back to RuleEdge format.
		//
		// The backend returns loopEdges in Transition format (from/to/when). Reverse
		// `loopEdgesToSaveTransitions`: Start/Continue/End sentinels are restored from `when`
		// and the off-body successor target (see `resolveInnerLoopTransitionTarget`).
		if (Array.isArray(ld.loopEdges)) {
			const loopNodeId = String(apiNode.id);
			const rawLoopEdges = ld.loopEdges as any[];
			const loopBodyNodeIds = new Set(((ld.loopNodes as RuleNode[]) ?? []).map((n) => String(n.id)));
			ld.loopEdges = rawLoopEdges.map((e: any): RuleEdge => {
				// Already in RuleEdge format — just stringify IDs.
				if (e.source != null || e.target != null) {
					return {
						...e,
						id: e.id != null ? String(e.id) : `edge__${e.source}-out-${e.target}`,
						source: e.source != null ? String(e.source) : e.source,
						target: e.target != null ? String(e.target) : e.target,
					} as RuleEdge;
				}

				// Transition format (from/to/when) — convert and restore sentinels.
				const from = String(e.from ?? "");
				const to   = String(e.to ?? "");
				const whenStr = String(e.when ?? "");
				const source = resolveInnerLoopTransitionSource(loopNodeId, from);
				const target = resolveInnerLoopTransitionTarget(
					loopNodeId,
					from,
					to,
					whenStr,
					loopBodyNodeIds,
				);
				const meta = metaForHydratedInnerLoopEdge(e, whenStr, target, source);
				const reconnectable = reconnectableForEdgeType(meta.type);
				const edgeScope = e.loopNodeId ?? loopNodeId;
				return {
					id: `edge__${source}-${meta.sourceHandle}-${target}`,
					source,
					sourceHandle: meta.sourceHandle,
					target,
					targetHandle: "in",
					type: meta.type,
					markerEnd: e.markerEnd ?? DEFAULT_LOOP_MARKER_END,
					...(reconnectable ? { reconnectable } : {}),
					...(edgeScope != null ? { data: { loopNodeId: edgeScope } } : {}),
				} as RuleEdge;
			});
			ld.loopEdges = normalizeLoopEdgesOffCanvasExitTargets(
				loopNodeId,
				(ld.loopNodes as RuleNode[]) ?? [],
				ld.loopEdges as RuleEdge[],
			);
		}
	}

	if (node.data.type === "reaction") {
		const typeId = apiNode.data.reactionTypeId;
		node.data.reactionTypeId = typeId != null ? String(typeId) : "";
		const fieldId = apiNode.data.screenFieldId;
		node.data.screenFieldId = fieldId != null ? String(fieldId) : "";
		const mcc = apiNode.data.minimumCharacterCount;
		if (mcc != null && mcc !== "") {
			const parsed = Number(mcc);
			if (!Number.isNaN(parsed)) {
				node.data.minimumCharacterCount = parsed;
			}
		}
		if (apiNode.data.showConfirmationText != null) {
			node.data.showConfirmationText = Boolean(apiNode.data.showConfirmationText);
		}
		if (apiNode.data.caseSensitive != null) {
			node.data.caseSensitive = Boolean(apiNode.data.caseSensitive);
		}
		if (typeof apiNode.data.userConfirmationText === "string") {
			node.data.userConfirmationText = apiNode.data.userConfirmationText;
		}
	}

	if (node.data.type === "clear_reaction") {
		const clearReactionTargetNodeId = apiNode.data.targetReactionNodeId;
		const clearReactionTargetNode = reactionNodes?.find((n) => String(n.id) === String(clearReactionTargetNodeId));
		node.data.reactionNodeId =
			clearReactionTargetNodeId != null ? String(clearReactionTargetNodeId) : undefined;
		node.data.reactionNodeName = clearReactionTargetNode?.data.name ?? "";
	}

	if (node.data.type === "event_listener") {
		const rawIds = apiNode.data.targetScreenFieldIds ?? apiNode.data.targets ?? [];
		node.data.targetScreenFieldIds = Array.isArray(rawIds)
			? rawIds.map((id: unknown) => String(id)).filter(Boolean)
			: [];
		node.data.action = apiNode.data.action ?? "click";
		if (Array.isArray(apiNode.data.targetScreenFieldNames)) {
			node.data.targetScreenFieldNames = apiNode.data.targetScreenFieldNames.map((n: unknown) =>
				String(n),
			);
		}
	}

	if (node.data.type === "action") {
		node.data.actionType = apiNode.data.actionType ?? apiNode.data.action ?? "click";
	}

	if (node.data.type === "stop") {
		node.data.tags = normalizeStopTagNames(apiNode.data?.tags);
	}

	return node as RuleNode;
};

const getValueCategory = (v: any): ResourceCategoryV2 => {
	if (v.valueCategory) return v.valueCategory;
	if (v.valueGlobalConstant) return "globalConstant";
	if (v.valueDatabaseTableFieldId) return "singleRowDbVariableField";
	if (v.valueScreenFieldId) return "screenField";
	if (v.valueSimpleVariableId) return "simpleVariable";
	if (v.valueFormulaVariableId) return "formulaVariable";
	if (v.valueDatabaseSingleRowVariableId) return "databaseSingleRowVariable";
	return "literal";
};

export const mapApiVariablesToState = (apiVariables: any): Partial<RuleVariablesState> => {
	if (!apiVariables) return {};
	const mapSimple = (v: any): RuleVariable => {
		const valueCategory = getValueCategory(v);
		return {
			id: String(v.id),
			name: v.name,
			type: "simple",
			dataTypeId: resolveApiDataTypeId(v.dataType),
			valueLiteral: v.valueLiteral,
			valueDatabaseTableFieldId:
				v.valueDatabaseTableFieldId != null ? String(v.valueDatabaseTableFieldId) : undefined,
			valueScreenFieldId: v.valueScreenFieldId != null ? String(v.valueScreenFieldId) : undefined,
			valueScreenField: mapApiScreenFieldRef(v.valueScreenField),
			valueDatabaseTableFieldName: v.valueDatabaseTableFieldName,
			valueScreenFieldName: v?.valueScreenField?.name,
			valueSimpleVariableName: v?.valueSimpleVariable?.name,
			valueSimpleVariableId: 
				v.valueSimpleVariableId != null ? String(v.valueSimpleVariableId) : undefined,
			valueFormulaVariableId:
				v.valueFormulaVariableId != null ? String(v.valueFormulaVariableId) : undefined,
			valueFormulaVariableName: v?.valueFormulaVariable?.name,
			valueFormulaVariable: mapApiFormulaVariableRef(v.valueFormulaVariable),
			valueDatabaseSingleRowVariableId:
				v.valueDatabaseSingleRowVariableId != null
					? String(v.valueDatabaseSingleRowVariableId)
					: undefined,
			valueDatabaseSingleRowVariableName: v.valueDatabaseSingleRowVariableName,
			valueGlobalConstant:
				v.valueGlobalConstant != null && v.valueGlobalConstant !== ""
					? typeof v.valueGlobalConstant === "object" && v.valueGlobalConstant !== null && "id" in v.valueGlobalConstant
						? String((v.valueGlobalConstant as { id: unknown }).id)
						: String(v.valueGlobalConstant)
					: undefined,
			valueGlobalConstantName:
				typeof v.valueGlobalConstantName === "string"
					? v.valueGlobalConstantName
					: typeof v.valueGlobalConstant === "object" &&
							v.valueGlobalConstant !== null &&
							typeof (v.valueGlobalConstant as { name?: unknown }).name === "string"
						? (v.valueGlobalConstant as { name: string }).name
						: undefined,
			valueCategory: valueCategory,
		};
	};

	const mapFormula = (v: any): RuleVariable => ({
		id: String(v.id),
		name: v.name,
		type: "formula",
		dataTypeId: resolveApiDataTypeId(v.dataType),
		variableFormula: normalizeVariableFormulaFromApi(v.variableFormula),
	});

	const mapDatabase = (v: any, type: "databaseSingle" | "databaseMulti"): RuleVariable => ({
		id: String(v.id),
		name: v.name,
		type: type,
		source: v.source ?? "",
		sourceNodeId: v.sourceNodeId ? String(v.sourceNodeId) : undefined,
		recordChoice: v.recordChoice === "all" ? "all" : v.recordChoice === "first" ? "first" : undefined,
	});

	return {
		simple: Array.isArray(apiVariables.simple) ? apiVariables.simple.map(mapSimple) : [],
		formula: Array.isArray(apiVariables.formula) ? apiVariables.formula.map(mapFormula) : [],
		databaseSingleRow: Array.isArray(apiVariables.databaseSingleRow)
			? apiVariables.databaseSingleRow.map((v: any) => mapDatabase(v, "databaseSingle"))
			: [],
		databaseMultiRow: Array.isArray(apiVariables.databaseMultiRow)
			? apiVariables.databaseMultiRow.map((v: any) => mapDatabase(v, "databaseMulti"))
			: [],
	};
};

const resolveOperatorId = (operatorId: any, operators: OperatorOption[]): string => {
	if (operatorId == null) return "";
	const match = operators.find((op) => String(op.id) === String(operatorId));
	return match?.operator ?? String(operatorId);
};

/** API may send `operator` as a string or as `{ id, operator }`. */
const normalizeAssignmentOperatorFromApi = (
	raw: unknown,
	operatorId: unknown,
	assignmentOperators: OperatorOption[],
): string => {
	if (typeof raw === "string") return raw;
	if (raw && typeof raw === "object" && "operator" in raw && typeof (raw as { operator?: unknown }).operator === "string") {
		return (raw as { operator: string }).operator;
	}
	const id = operatorId ?? (raw && typeof raw === "object" && "id" in raw ? (raw as { id?: unknown }).id : undefined);
	return resolveOperatorId(id, assignmentOperators);
};

const resolveApiDataTypeId = (dataType: any): number | undefined => {
	if (dataType?.id != null) return Number(dataType.id);
	return undefined;
};
